import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export default function StatusTooltip({
  isDark = false,
  message,
  lastError,
  children
}) {
  const triggerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUpward: false });

  useEffect(() => {
    if (!visible) return;
    const handleScrollOrResize = () => updatePosition();

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [visible]);

  if (!message && !lastError) return children;

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 300;
    const tooltipEstimatedHeight = 130;

    const shouldOpenUpward = window.innerHeight - rect.bottom < tooltipEstimatedHeight + 10;

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    if (left < 16) left = 16;
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }

    setCoords({
      left,
      top: shouldOpenUpward ? rect.top - 8 : rect.bottom + 8,
      openUpward: shouldOpenUpward,
    });
  };

  const handleMouseEnter = () => {
    updatePosition();
    setVisible(true);
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  const tooltipBg = isDark ? 'bg-[#1c1c1c]' : 'bg-gray-50';
  const tooltipBorder = isDark ? '!border-[#292929]' : '!border-gray-200';
  const tooltipTextColor = isDark ? 'text-white' : 'text-[#292929]';
  const subTextColor = isDark ? 'text-[#a3a3a3]' : 'text-[#595959]';
  const dividerBorder = isDark ? 'border-[#292929]' : 'border-gray-200';

  return (
    <>
      <div
        ref={triggerRef}
        className="relative inline-flex items-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {visible && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${coords.left}px`,
            top: `${coords.top}px`,
            transform: coords.openUpward ? 'translateY(-100%)' : 'none',
            zIndex: 99999,
          }}
          className={cn(
            "w-[300px] max-w-[calc(100vw-32px)] rounded-[6px] !border-solid p-[10px] shadow-[0_8px_16px_rgba(2,16,24,0.24)] pointer-events-none text-left",
            tooltipBg,
            tooltipBorder
          )}
        >
          {message && (
            <p className={cn("text-[13px] font-semibold leading-[1.4] whitespace-normal break-words m-0", tooltipTextColor)}>
              {message}
            </p>
          )}

          {lastError && (
            <div className={cn(message ? `mt-1.5 p-2 border-solid rounded ${dividerBorder}` : "")}>
              {message && (
                <span className={cn("text-[11px] font-medium uppercase tracking-wider block mb-0.5", subTextColor)}>
                  Details
                </span>
              )}
              <p className={cn("text-[12px] font-normal leading-[1.35] whitespace-normal break-words m-0", subTextColor)}>
                {lastError}
              </p>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}